import { RefObject, useEffect, useState } from 'react';
import { ChartMode } from '@employ-engage/data';

/**
 * Which palette a chart should draw itself in.
 *
 * Dark mode is applied by `ThemeWrapper` as a `dark` class on a wrapper
 * `div`, **not** on `<html>`, so reading `document.documentElement` would
 * answer "light" forever. This asks the element itself what it is inside,
 * which is correct wherever the class is put and needs no context threading
 * through every chart's parents.
 *
 * Note that dark mode is currently switched off at the source —
 * `theme-wrapper.tsx` initialises to LIGHT behind a TODO — so today this
 * reports 'light' unless something calls `selectDarkTheme`. It is written to
 * be right when that is turned on rather than to be rewritten then.
 */
export const useChartMode = (ref: RefObject<HTMLElement | null>): ChartMode => {
  const [mode, setMode] = useState<ChartMode>('light');

  useEffect(() => {
    const read = () =>
      setMode(ref.current?.closest('.dark') ? 'dark' : 'light');

    read();

    // Theme changes are rare and the handler is one `closest` call, so
    // watching class changes across the tree costs nothing worth measuring.
    // Reacting to nothing would mean a chart keeping the palette it was
    // mounted with after the theme is toggled under it.
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
    return () => observer.disconnect();
  }, [ref]);

  return mode;
};

export default useChartMode;
